import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Typography,
  Box,
  Paper,
  useTheme,
  Card,
  CardContent,
  Fade,
  alpha,
} from "@mui/material";
import { FilterList, Add, Delete } from "@mui/icons-material";
import { ItemFilterRule } from "../types/AdHocTypes";
import { itemInfoFields } from "../config/FilterConfigs";

interface ItemFilterModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (rules: ItemFilterRule[]) => void;
  initialRules: ItemFilterRule[];
}

const ItemFilterModal: React.FC<ItemFilterModalProps> = ({ open, onClose, onSave, initialRules }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [rules, setRules] = useState<ItemFilterRule[]>([]);
  const [selectedField, setSelectedField] = useState("");
  const [fieldValue, setFieldValue] = useState("");
  const [operator, setOperator] = useState<"INCLUDE" | "EXCLUDE">("INCLUDE");
  const [selectedRuleIndex, setSelectedRuleIndex] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setRules([...initialRules]);
      // Reset form
      setSelectedField("");
      setFieldValue("");
      setOperator("INCLUDE");
      setSelectedRuleIndex(null);
    }
  }, [open, initialRules]);

  const addRule = () => {
    const selectedFieldObj = itemInfoFields.find((f) => f.id === selectedField);

    if (!selectedFieldObj || !fieldValue.trim()) {
      return;
    }

    const newRule: ItemFilterRule = {
      fieldId: selectedField,
      fieldLabel: t(selectedFieldObj.labelKey),
      operator,
      value: fieldValue.trim(),
    };

    setRules([...rules, newRule]);

    // Reset form
    setSelectedField("");
    setFieldValue("");
    setOperator("INCLUDE");
  };

  const handleRuleClick = (index: number) => {
    setSelectedRuleIndex(selectedRuleIndex === index ? null : index);
  };

  const handleSave = () => {
    onSave(rules);
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: theme.spacing(3),
          background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha(
            theme.palette.background.default,
            0.95
          )} 100%)`,
          backdropFilter: "blur(20px)",
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: `0 24px 48px ${alpha(theme.palette.common.black, 0.15)}`,
          minHeight: "70vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          padding: theme.spacing(3),
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(
            theme.palette.secondary.main,
            0.1
          )} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: "white",
            }}
          >
            <FilterList />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {t("adHocSegments.defineItemFilter")}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: theme.spacing(4) }}>
        <Fade in timeout={800}>
          <Grid container spacing={4}>
            {/* Field Selection Card */}
            <Grid item xs={12}>
              <Card
                elevation={2}
                sx={{
                  borderRadius: theme.spacing(2),
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha(
                    theme.palette.background.default,
                    0.3
                  )} 100%)`,
                }}
              >
                <CardContent sx={{ padding: theme.spacing(3) }}>
                  <Typography
                    variant="h6"
                    sx={{
                      marginBottom: theme.spacing(3),
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                    }}
                  >
                    {t("adHocSegments.defineItemFilter")}
                  </Typography>

                  <Grid container spacing={3}>
                    {/* Field Selection */}
                    <Grid item xs={12} md={3}>
                      <Typography variant="subtitle2" sx={{ marginBottom: 1, fontWeight: 600 }}>
                        {t("adHocSegments.selectField")}
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={selectedField}
                          onChange={(e) => setSelectedField(e.target.value)}
                          displayEmpty
                          sx={{
                            borderRadius: theme.spacing(1.5),
                            background: alpha(theme.palette.background.default, 0.5),
                          }}
                        >
                          <MenuItem value="">{t("adHocSegments.chooseField")}</MenuItem>
                          {itemInfoFields.map((field) => (
                            <MenuItem key={field.id} value={field.id}>
                              {t(field.labelKey)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Filter Type */}
                    <Grid item xs={12} md={3}>
                      <Typography variant="subtitle2" sx={{ marginBottom: 1, fontWeight: 600 }}>
                        {t("adHocSegments.filterType")}
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={operator}
                          onChange={(e) => setOperator(e.target.value as "INCLUDE" | "EXCLUDE")}
                          sx={{
                            borderRadius: theme.spacing(1.5),
                            background: alpha(theme.palette.background.default, 0.5),
                          }}
                        >
                          <MenuItem value="INCLUDE">{t("adHocSegments.includeArticle")}</MenuItem>
                          <MenuItem value="EXCLUDE">{t("adHocSegments.excludeArticle")}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Value Input */}
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" sx={{ marginBottom: 1, fontWeight: 600 }}>
                        {t("adHocSegments.valueFreeText")}
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={t("adHocSegments.enterArticleValue")}
                        value={fieldValue}
                        onChange={(e) => setFieldValue(e.target.value)}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: theme.spacing(1.5),
                            background: alpha(theme.palette.background.default, 0.5),
                          },
                        }}
                      />
                    </Grid>

                    {/* Add Button */}
                    <Grid item xs={12} md={2}>
                      <Typography variant="subtitle2" sx={{ marginBottom: 1, fontWeight: 600, visibility: "hidden" }}>
                        Action
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={addRule}
                        disabled={!selectedField || !fieldValue.trim()}
                        fullWidth
                        startIcon={<Add />}
                        sx={{
                          borderRadius: theme.spacing(1.5),
                          textTransform: "uppercase",
                          fontWeight: 600,
                          fontSize: "0.875rem",
                          padding: theme.spacing(1.2, 2),
                          color: theme.palette.primary.contrastText,
                          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                          boxShadow: `0 3px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                          "&:hover": {
                            color: theme.palette.primary.contrastText,
                            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                            boxShadow: `0 5px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                            transform: "translateY(-1px)",
                          },
                          "&:disabled": {
                            background: alpha(theme.palette.action.disabled, 0.2),
                            color: theme.palette.action.disabled,
                            boxShadow: "none",
                          },
                        }}
                      >
                        {t("adHocSegments.addItemRule")}
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Rules List Card */}
            <Grid item xs={12}>
              <Card
                elevation={2}
                sx={{
                  borderRadius: theme.spacing(2),
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha(
                    theme.palette.background.default,
                    0.3
                  )} 100%)`,
                }}
              >
                <CardContent sx={{ padding: theme.spacing(3) }}>
                  <Box sx={{ display: "flex", alignItems: "center", marginBottom: theme.spacing(3) }}>
                    <FilterList sx={{ marginRight: theme.spacing(1), color: theme.palette.primary.main }} />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                      }}
                    >
                      {t("adHocSegments.currentItemRules")} ({rules.length})
                    </Typography>
                  </Box>

                  {rules.length === 0 ? (
                    <Paper
                      sx={{
                        padding: theme.spacing(4),
                        textAlign: "center",
                        borderRadius: theme.spacing(2),
                        background: alpha(theme.palette.background.default, 0.3),
                        border: `1px dashed ${alpha(theme.palette.divider, 0.3)}`,
                      }}
                    >
                      <Typography color="text.secondary" variant="body1">
                        {t("adHocSegments.noItemRules")}
                      </Typography>
                    </Paper>
                  ) : (
                    <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
                      {rules.map((rule, index) => (
                        <Paper
                          key={index}
                          elevation={1}
                          sx={{
                            padding: theme.spacing(2.5),
                            marginBottom: theme.spacing(2),
                            borderRadius: theme.spacing(1.5),
                            border:
                              selectedRuleIndex === index
                                ? `2px solid ${theme.palette.primary.main}`
                                : `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                            cursor: "pointer",
                            transition: "all 0.2s ease-in-out",
                            background:
                              selectedRuleIndex === index
                                ? alpha(theme.palette.primary.main, 0.05)
                                : theme.palette.background.paper,
                            "&:hover": {
                              boxShadow: theme.shadows[3],
                              transform: "translateY(-1px)",
                            },
                          }}
                          onClick={() => handleRuleClick(index)}
                        >
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={4}>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                              >
                                {t("adHocSegments.fieldName")}
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis" }}
                              >
                                {rule.fieldLabel}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                              >
                                {t("adHocSegments.type")}
                              </Typography>
                              <Typography
                                variant="body2"
                                color={rule.operator === "INCLUDE" ? "success.main" : "warning.main"}
                                sx={{ fontWeight: 500 }}
                              >
                                {rule.operator === "INCLUDE" ? t("adHocSegments.include") : t("adHocSegments.exclude")}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                              >
                                {t("adHocSegments.value")}
                              </Typography>
                              <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                                {rule.value}
                              </Typography>
                            </Grid>
                            <Grid item xs={1}>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const newRules = rules.filter((_, i) => i !== index);
                                  setRules(newRules);
                                  setSelectedRuleIndex(null);
                                }}
                                startIcon={<Delete />}
                                sx={{
                                  borderRadius: theme.spacing(1),
                                  textTransform: "uppercase",
                                  fontWeight: 500,
                                  fontSize: "0.75rem",
                                  minWidth: "auto",
                                  padding: theme.spacing(0.5, 1),
                                  "&:hover": {
                                    borderColor: theme.palette.error.main,
                                    background: alpha(theme.palette.error.main, 0.1),
                                  },
                                }}
                              >
                                {t("common.delete")}
                              </Button>
                            </Grid>
                          </Grid>
                        </Paper>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Fade>
      </DialogContent>

      <DialogActions
        sx={{
          padding: theme.spacing(3),
          background: `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(
            theme.palette.background.default,
            0.9
          )} 100%)`,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          backdropFilter: "blur(10px)",
          gap: theme.spacing(2),
        }}
      >
        <Button
          onClick={handleCancel}
          variant="outlined"
          sx={{
            borderRadius: theme.spacing(1.5),
            textTransform: "uppercase",
            fontWeight: 600,
            fontSize: "0.875rem",
            padding: theme.spacing(1.2, 3),
            borderColor: alpha(theme.palette.text.primary, 0.3),
            color: theme.palette.text.primary,
            "&:hover": {
              borderColor: theme.palette.text.primary,
              background: alpha(theme.palette.text.primary, 0.05),
            },
          }}
        >
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            borderRadius: theme.spacing(1.5),
            textTransform: "uppercase",
            fontWeight: 600,
            fontSize: "0.875rem",
            padding: theme.spacing(1.2, 3),
            color: theme.palette.primary.contrastText,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: `0 4px 15px ${alpha(theme.palette.primary.main, 0.4)}`,
            "&:hover": {
              color: theme.palette.primary.contrastText,
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.6)}`,
              transform: "translateY(-1px)",
            },
          }}
        >
          {t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ItemFilterModal;
